import {
  InformationCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';

import useGetAssessmentDetails from 'hooks/useGetAssessmentDetails';
import { t } from 'locales/i18n';

export enum BannerVariant {
  INFO = 'info',
  ERROR = 'error',
}
interface BannerProps {
  contactLink?: boolean;
  message: string | React.ReactNode;
  requestChangesTo?: string;
  variant: BannerVariant;
  bannerDataTestId?: string;
}

const Banner = ({
  contactLink,
  message,
  requestChangesTo,
  variant,
  bannerDataTestId,
}: BannerProps) => {
  const [value, setValue] = useState('');
  const { interviewUid } = useParams();
  const { data } = useGetAssessmentDetails(interviewUid ?? '');

  const assessmentName = data?.name;

  useEffect(() => {
    if (assessmentName === undefined || value === '') {
      return;
    }
    window.Intercom(
      'showNewMessage',
      `Hello! I'd like to request changes to my ${value} settings for ${assessmentName}.`
    );
  }, [value]);

  const backgroundColor = twMerge(
    `${variant === BannerVariant.INFO ? 'bg-kira-violet-100' : ''}`,
    `${variant === BannerVariant.ERROR ? 'bg-kira-red-100' : ''}`
  );
  const iconColor = twMerge(
    `${variant === BannerVariant.INFO ? 'text-kira-violet-900' : ''}`,
    `${variant === BannerVariant.ERROR ? 'text-kira-red-800' : ''}`
  );

  const iconClassName = twMerge(
    'h-4',
    'inline-block',
    'mx-2',
    'self-center',
    'w-4',
    iconColor
  );

  return (
    <div
      className={`prose-body-sm-reg !mb-4 flex h-9 w-full self-center rounded px-2 py-3 ${backgroundColor}`}
    >
      {variant === BannerVariant.INFO ? (
        <InformationCircleIcon className={iconClassName} />
      ) : (
        <ExclamationCircleIcon className={iconClassName} />
      )}
      <p className="inline-block self-center" data-testId={bannerDataTestId}>
        {message}{' '}
        {contactLink && requestChangesTo && (
          <>
            {t('To request changes, please')}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => setValue(requestChangesTo)}
            >
              {t('contact our Client Success team.')}
            </button>
          </>
        )}
      </p>
    </div>
  );
};

export default Banner;
